import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Typography, Flex, Button, Tag, Modal, Form, Input, DatePicker, TimePicker, Select, Checkbox, Radio, message, Table, Card, Spin, Empty } from "antd";
import { PlusOutlined, LeftOutlined, RightOutlined, CheckOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Calendar03Icon, Task01Icon, Video01Icon, Call02Icon } from "hugeicons-react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import { getEmployeeActivities, getLeadActivities, createActivity, updateActivity, getAllActivities } from "../../../services/activityService";
import { getEmployees } from "../../../services/employeeService";

const { Title, Text } = Typography;

const SUB_TABS = [
  { key: "calendar", label: "Calendar", icon: Calendar03Icon },
  { key: "tasks", label: "Tasks", icon: Task01Icon },
  { key: "meetings", label: "Meetings", icon: Video01Icon },
  { key: "calls", label: "Calls", icon: Call02Icon },
];

const PILL_STYLES = {
  meeting: { background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe" },
  call: { background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" },
  task: { background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" },
};

const getPillStyle = (type) => PILL_STYLES[type] || PILL_STYLES.task;

const getInitialActivityType = (tab) => {
  if (tab === "meetings") return "meeting";
  if (tab === "calls") return "call";
  return "task";
};

const getDayCellBg = (day) => {
  if (day.isToday) return "#eff6ff";
  if (day.isCurrentMonth) return "#ffffff";
  return "#f8fafc";
};

const getDayDateColor = (day) => {
  if (day.isToday) return "#ffffff";
  if (day.isCurrentMonth) return "#1e293b";
  return "#94a3b8";
};

const mapBackendActivity = (act) => {
  const d = act.activityDate ? dayjs(act.activityDate) : dayjs(act.createdAt || undefined);
  return {
    id: act._id || act.id || `act-${Math.random()}`,
    _id: act._id,
    type: act.type || "task",
    title: act.title || "Activity",
    description: act.description || "",
    date: d.format("YYYY-MM-DD"),
    time: d.format("hh:mm A"),
    status: act.status || "pending",
    companyName: act.companyName || act.leadCompanyName || "",
    contactName: act.contactName || "",
    assignedTo: act.assignedTo || "Team Member",
    assignedEmployeeId: act.employeeId || "",
    leadId: act.leadId || "",
  };
};

const generateMonthDays = (currentDate) => {
  const startOfMonth = currentDate.startOf("month");
  const endOfMonth = currentDate.endOf("month");
  const startDayOfWeek = startOfMonth.day() === 0 ? 6 : startOfMonth.day() - 1;
  const daysInMonth = currentDate.daysInMonth();
  const days = [];

  for (let i = startDayOfWeek; i > 0; i--) {
    const d = startOfMonth.subtract(i, "day");
    days.push({ date: d, isCurrentMonth: false, isToday: d.isSame(dayjs(), "day"), isPast: d.isBefore(dayjs(), "day") });
  }
  for (let i = 0; i < daysInMonth; i++) {
    const d = startOfMonth.add(i, "day");
    days.push({ date: d, isCurrentMonth: true, isToday: d.isSame(dayjs(), "day"), isPast: d.isBefore(dayjs(), "day") });
  }
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      const d = endOfMonth.add(i, "day");
      days.push({ date: d, isCurrentMonth: false, isToday: d.isSame(dayjs(), "day"), isPast: d.isBefore(dayjs(), "day") });
    }
  }
  return days;
};

const generateWeekDays = (currentDate) => {
  const startOfWeek = currentDate.startOf("week").add(1, "day");
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = startOfWeek.add(i, "day");
    days.push({ date: d, isCurrentMonth: true, isToday: d.isSame(dayjs(), "day"), isPast: d.isBefore(dayjs(), "day") });
  }
  return days;
};

const generateCalendarDays = (currentDate, calendarView) => {
  if (calendarView === "month") return generateMonthDays(currentDate);
  if (calendarView === "week") return generateWeekDays(currentDate);
  return [{ date: currentDate, isCurrentMonth: true, isToday: currentDate.isSame(dayjs(), "day"), isPast: currentDate.isBefore(dayjs(), "day") }];
};

function ActivitiesCalendar({ role = "employee", employeeId = null, lead = null, company = null, embedded = false, subTab: propSubTab, onSubTabChange, modalTrigger = 0, hideHeaderRail = false }) {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";
  const effectiveRole = role || user?.role || getCookie("role") || "employee";
  const effectiveEmployeeId = employeeId || user?._id || user?.id || getCookie("employeeId") || getCookie("userId") || "";
  const isAdmin = effectiveRole === "admin" || effectiveRole === "company_admin";

  const targetCompanyName = company?.name || lead?.leadCompanyName || lead?.companyName || "";
  const targetContactName = lead?.contactName || "";

  const [internalSubTab, setInternalSubTab] = useState("calendar");
  const subTab = propSubTab !== undefined ? propSubTab : internalSubTab;
  const setSubTab = (val) => {
    setInternalSubTab(val);
    if (onSubTabChange) onSubTabChange(val);
  };
  const [calendarView, setCalendarView] = useState("month");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [filterTypes, setFilterTypes] = useState({
    task: true,
    meeting: true,
    call: true,
  });

  const [activities, setActivities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeFilters, setEmployeeFilters] = useState({});
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin || !effectiveCompanyCode) return;
    getEmployees(effectiveCompanyCode)
      .then((res) => {
        if (res?.success && Array.isArray(res.employees)) {
          const adminId = effectiveEmployeeId || "admin";
          const adminEntry = {
            _id: adminId,
            id: adminId,
            name: user?.name ? `${user.name} (Admin)` : "Admin (You)",
            role: "admin",
          };
          const otherEmps = res.employees.filter((e) => (e._id || e.id) !== adminId);
          const combined = [adminEntry, ...otherEmps];
          setEmployees(combined);

          const initialMap = {};
          combined.forEach((emp) => {
            const id = emp._id || emp.id;
            if (id) initialMap[id] = true;
          });
          setEmployeeFilters(initialMap);
        }
      })
      .catch((err) => console.error("[ActivitiesCalendar] Error loading employees:", err));
  }, [isAdmin, effectiveCompanyCode, effectiveEmployeeId, user?.name]);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearchQuery.trim()) return employees;
    const q = employeeSearchQuery.toLowerCase().trim();
    return employees.filter((emp) => {
      const name = (emp.name || `${emp.firstName || ""} ${emp.lastName || ""}` || emp.email || "").toLowerCase();
      return name.includes(q);
    });
  }, [employees, employeeSearchQuery]);

  const allEmployeesChecked = useMemo(() => {
    if (employees.length === 0) return true;
    return employees.every((emp) => employeeFilters[emp._id || emp.id] !== false);
  }, [employees, employeeFilters]);

  const isEmployeeIndeterminate = useMemo(() => {
    if (employees.length === 0) return false;
    const selectedCount = employees.filter((emp) => employeeFilters[emp._id || emp.id] !== false).length;
    return selectedCount > 0 && selectedCount < employees.length;
  }, [employees, employeeFilters]);

  const handleToggleAllEmployees = (checked) => {
    const nextMap = {};
    employees.forEach((emp) => {
      const id = emp._id || emp.id;
      if (id) nextMap[id] = checked;
    });
    setEmployeeFilters(nextMap);
  };

  const handleToggleEmployee = (empId, checked) => {
    setEmployeeFilters((prev) => ({
      ...prev,
      [empId]: checked,
    }));
  };

  const fetchActivities = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      setLoading(true);
      if (lead?._id) {
        const res = await getLeadActivities(effectiveCompanyCode, lead._id);
        setActivities(res?.success && Array.isArray(res.data) ? res.data.map(mapBackendActivity) : []);
        return;
      }

      if (isAdmin) {
        const empMap = {};
        if (effectiveEmployeeId) {
          empMap[effectiveEmployeeId] = user?.name ? `${user.name} (Admin)` : "Admin (You)";
        }
        employees.forEach((emp) => {
          const id = emp._id || emp.id;
          if (id) {
            empMap[id] = emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Team Member";
          }
        });

        const res = await getAllActivities(effectiveCompanyCode);
        if (res?.success && Array.isArray(res.data)) {
          const enriched = res.data.map((act) => ({
            ...act,
            assignedTo: act.assignedTo || empMap[act.employeeId] || "Team Member",
          }));
          setActivities(enriched.map(mapBackendActivity));
        } else {
          setActivities([]);
        }
      } else if (effectiveEmployeeId) {
        const res = await getEmployeeActivities(effectiveCompanyCode, effectiveEmployeeId);
        setActivities(res?.success && Array.isArray(res.data) ? res.data.map(mapBackendActivity) : []);
      }
    } catch (err) {
      console.error("[ActivitiesCalendar] Error fetching activities:", err);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, lead?._id, isAdmin, employees, effectiveEmployeeId, user?.name]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const scopedActivities = useMemo(() => {
    let list = activities;
    if (targetCompanyName) {
      const q = targetCompanyName.toLowerCase();
      list = list.filter((a) => a.companyName?.toLowerCase().includes(q));
    }
    if (isAdmin && Object.keys(employeeFilters).length > 0) {
      list = list.filter((a) => {
        if (!a.assignedEmployeeId) return true;
        return employeeFilters[a.assignedEmployeeId] !== false;
      });
    }
    return list;
  }, [activities, targetCompanyName, isAdmin, employeeFilters]);

  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleOpenModal = useCallback(
    (targetDate = null) => {
      form.setFieldsValue({
        type: getInitialActivityType(subTab),
        employeeId: effectiveEmployeeId || (employees[0]?._id || employees[0]?.id),
        date: targetDate ? dayjs(targetDate) : dayjs(),
        time: dayjs("10:00 AM", "hh:mm A"),
        title: "",
        description: "",
        companyName: targetCompanyName || "",
        contactName: targetContactName || "",
      });
      setModalOpen(true);
    },
    [form, subTab, targetCompanyName, targetContactName, effectiveEmployeeId, employees],
  );

  const handleSaveActivity = async (values) => {
    try {
      setSaving(true);
      const datePart = values.date.format("YYYY-MM-DD");
      const timePart = values.time ? values.time.format("HH:mm:ss") : "10:00:00";
      const activityDate = new Date(`${datePart}T${timePart}`);

      const payload = {
        employeeId: values.employeeId || effectiveEmployeeId,
        leadId: lead?._id || undefined,
        type: values.type,
        title: values.title.trim(),
        description: values.description ? values.description.trim() : "",
        activityDate: activityDate.toISOString(),
      };

      const res = await createActivity(effectiveCompanyCode, payload);
      if (res?.success !== false) {
        message.success("Activity scheduled successfully!");
        setModalOpen(false);
        fetchActivities();
      } else {
        message.error(res?.message || "Failed to save activity.");
      }
    } catch (err) {
      console.error("[ActivitiesCalendar] Error saving activity:", err);
      message.error("Failed to save activity.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (act, e) => {
    e?.stopPropagation();
    const nextStatus = act.status === "completed" ? "pending" : "completed";
    try {
      if (act._id) {
        await updateActivity(effectiveCompanyCode, act._id, { status: nextStatus });
      }
      setActivities((prev) => prev.map((a) => (a.id === act.id ? { ...a, status: nextStatus } : a)));
      message.success(`Activity marked as ${nextStatus}!`);
    } catch (err) {
      console.error("[ActivitiesCalendar] Error toggling activity:", err);
      message.error("Failed to update activity status.");
    }
  };

  const calendarDays = useMemo(() => generateCalendarDays(currentDate, calendarView), [currentDate, calendarView]);

  const handlePrevPeriod = () => {
    if (calendarView === "month") setCurrentDate((d) => d.subtract(1, "month"));
    else if (calendarView === "week") setCurrentDate((d) => d.subtract(1, "week"));
    else setCurrentDate((d) => d.subtract(1, "day"));
  };

  const handleNextPeriod = () => {
    if (calendarView === "month") setCurrentDate((d) => d.add(1, "month"));
    else if (calendarView === "week") setCurrentDate((d) => d.add(1, "week"));
    else setCurrentDate((d) => d.add(1, "day"));
  };

  const getEventsForDay = (d) => {
    const dStr = d.format("YYYY-MM-DD");
    return scopedActivities.filter((a) => a.date === dStr && filterTypes[a.type]);
  };

  const activitiesForList = useMemo(() => {
    if (subTab === "tasks") return scopedActivities.filter((a) => a.type === "task");
    if (subTab === "meetings") return scopedActivities.filter((a) => a.type === "meeting");
    if (subTab === "calls") return scopedActivities.filter((a) => a.type === "call");
    return scopedActivities;
  }, [scopedActivities, subTab]);

  const listColumns = [
    {
      title: "Date & Time",
      key: "datetime",
      width: 160,
      render: (_, r) => (
        <Flex vertical gap={2}>
          <Text strong style={{ color: "#1e293b", fontSize: "0.88rem" }}>
            {dayjs(r.date).format("DD MMM, YYYY")}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {r.time}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (t) => <Text style={{ color: "#334155", fontWeight: 500 }}>{t || "—"}</Text>,
    },
    {
      title: "Client / Contact",
      dataIndex: "contactName",
      key: "contactName",
      render: (t) => (
        <Text strong style={{ color: "#0f172a" }}>
          {t || "—"}
        </Text>
      ),
    },
    ...(isAdmin
      ? [
          {
            title: "Assigned To",
            dataIndex: "assignedTo",
            key: "assignedTo",
            render: (t) => (
              <Tag icon={<UserOutlined />} color="cyan" style={{ borderRadius: 4 }}>
                {t || "Team Member"}
              </Tag>
            ),
          },
        ]
      : []),
    {
      title: "Activity Title & Details",
      key: "details",
      render: (_, r) => (
        <Flex vertical gap={2}>
          <Text strong style={{ color: "#1e293b" }}>
            {r.title}
          </Text>
          {r.description && (
            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
              {r.description}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "right",
      width: 140,
      render: (_, r) => (
        <Button
          size="small"
          type={r.status === "completed" ? "default" : "primary"}
          icon={r.status === "completed" ? <CheckOutlined /> : null}
          onClick={(e) => handleToggleComplete(r, e)}
          style={{
            borderRadius: 20,
            fontSize: "0.8rem",
            fontWeight: 600,
            background: r.status === "completed" ? "#f1f5f9" : "#2459d3",
            color: r.status === "completed" ? "#059669" : "#ffffff",
            borderColor: r.status === "completed" ? "#cbd5e1" : "#2459d3",
          }}
        >
          {r.status === "completed" ? "Completed" : "Mark Done"}
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (modalTrigger > 0) {
      handleOpenModal();
    }
  }, [modalTrigger, handleOpenModal]);

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Sub Tabs Navigation Header ── */}
      {!hideHeaderRail && (
        <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 14 }}>
          <Radio.Group value={subTab} onChange={(e) => setSubTab(e.target.value)} buttonStyle="solid" size="middle">
            {SUB_TABS.map((t) => {
              const IconComp = t.icon;
              return (
                <Radio.Button key={t.key} value={t.key} style={{ padding: "0 14px" }}>
                  <IconComp size={16} strokeWidth={2} style={{ marginRight: 6, verticalAlign: "middle" }} />
                  {t.label}
                </Radio.Button>
              );
            })}
          </Radio.Group>

          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()} style={{ borderRadius: 8, backgroundColor: "#2459d3", fontWeight: 600 }}>
            Schedule Activity
          </Button>
        </Flex>
      )}

      {/* ── Sub-view: CALENDAR ── */}
      {subTab === "calendar" && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: 20 }}>
          {/* Main Grid View */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              minWidth: 0,
            }}
          >
            {/* Calendar Controls */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
              <Flex align="center" gap={12}>
                <Button size="small" onClick={handlePrevPeriod} icon={<LeftOutlined />} />
                <Title level={4} style={{ margin: 0, minWidth: 160, color: "#1e293b", fontSize: "1.05rem" }}>
                  {currentDate.format("MMMM YYYY")}
                </Title>
                <Button size="small" onClick={handleNextPeriod} icon={<RightOutlined />} />
                <Button size="small" onClick={() => setCurrentDate(dayjs())}>
                  Today
                </Button>
              </Flex>

              <Radio.Group value={calendarView} onChange={(e) => setCalendarView(e.target.value)} size="small" buttonStyle="solid">
                <Radio.Button value="month">Month</Radio.Button>
                <Radio.Button value="week">Week</Radio.Button>
              </Radio.Group>
            </Flex>

            <Spin spinning={loading}>
              {/* Month Day Headers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  textAlign: "center",
                  borderBottom: "1px solid #e2e8f0",
                  paddingBottom: 8,
                }}
              >
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <Text key={d} strong style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {d}
                  </Text>
                ))}
              </div>

              {/* Day Cells Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 4,
                  minHeight: 480,
                }}
              >
                {calendarDays.map((dayItem, idx) => {
                  const dayEvents = getEventsForDay(dayItem.date);
                  return (
                    <div
                      key={idx}
                      onClick={() => handleOpenModal(dayItem.date)}
                      style={{
                        minHeight: 85,
                        minWidth: 0,
                        background: getDayCellBg(dayItem),
                        border: "1px solid #f1f5f9",
                        borderRadius: 8,
                        padding: "6px 8px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        overflow: "hidden",
                        boxSizing: "border-box",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = getDayCellBg(dayItem))}
                    >
                      <Flex justify="space-between" align="center">
                        <span
                          style={{
                            fontSize: "0.78rem",
                            fontWeight: dayItem.isToday ? 700 : 500,
                            color: getDayDateColor(dayItem),
                            background: dayItem.isToday ? "#2459d3" : "transparent",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {dayItem.date.date()}
                        </span>
                        {dayEvents.length > 0 && <span style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600 }}>{dayEvents.length}</span>}
                      </Flex>

                      {/* Day Events Pills */}
                      <Flex vertical gap={3} style={{ overflow: "hidden", width: "100%", minWidth: 0 }}>
                        {dayEvents.slice(0, 3).map((evt) => {
                          const style = getPillStyle(evt.type);
                          return (
                            <div
                              key={evt.id}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              style={{
                                ...style,
                                borderRadius: 4,
                                padding: "2px 5px",
                                fontSize: "0.7rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                width: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                                textDecoration: evt.status === "completed" ? "line-through" : "none",
                                opacity: evt.status === "completed" ? 0.6 : 1,
                              }}
                              title={`${evt.title} (${evt.time})`}
                            >
                              <span style={{ fontWeight: 600, flexShrink: 0 }}>{evt.time?.split(" ")[0]}</span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{evt.title}</span>
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <Text type="secondary" style={{ fontSize: "0.68rem", textAlign: "center" }}>
                            +{dayEvents.length - 3} more
                          </Text>
                        )}
                      </Flex>
                    </div>
                  );
                })}
              </div>
            </Spin>
          </div>

          {/* Right Sidebar: Filters & Legend */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 18,
              height: "fit-content",
            }}
          >
            {isAdmin && employees.length > 0 && (
              <div style={{ marginBottom: 18, borderBottom: "1px solid #f1f5f9", paddingBottom: 16 }}>
                <Text strong style={{ fontSize: "0.85rem", color: "#0f172a", display: "block", marginBottom: 10 }}>
                  Employees
                </Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1.5px solid #cbd5e1",
                    padding: "4px 0",
                    marginBottom: 12,
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderBottomColor = "#2459d3";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderBottomColor = "#cbd5e1";
                  }}
                >
                  <SearchOutlined style={{ color: "#94a3b8", fontSize: "0.8rem", marginRight: 6, flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={employeeSearchQuery}
                    onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      background: "transparent",
                      width: "100%",
                      fontSize: "0.8rem",
                      color: "#1e293b",
                      padding: 0,
                    }}
                  />
                  {employeeSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setEmployeeSearchQuery("")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        fontSize: "0.9rem",
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Checkbox indeterminate={isEmployeeIndeterminate} checked={allEmployeesChecked} onChange={(e) => handleToggleAllEmployees(e.target.checked)}>
                    <span style={{ fontWeight: 600, fontSize: "0.82rem", color: "#334155" }}>All Employees</span>
                  </Checkbox>
                </div>
                <Flex vertical gap={8} style={{ maxHeight: 185, overflowY: "auto", paddingRight: 4 }}>
                  {filteredEmployees.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: "0.78rem", textAlign: "center", padding: "8px 0" }}>
                      No employees found
                    </Text>
                  ) : (
                    filteredEmployees.map((emp) => {
                      const empId = emp._id || emp.id;
                      const empName = emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Employee";
                      const isChecked = employeeFilters[empId] !== false;
                      return (
                        <Checkbox key={empId} checked={isChecked} onChange={(e) => handleToggleEmployee(empId, e.target.checked)}>
                          <span style={{ fontSize: "0.8rem", color: "#475569" }} title={empName}>
                            {empName}
                          </span>
                        </Checkbox>
                      );
                    })
                  )}
                </Flex>
              </div>
            )}

            <Text strong style={{ fontSize: "0.85rem", color: "#0f172a", display: "block", marginBottom: 14 }}>
              Activity Types
            </Text>
            <Flex vertical gap={12}>
              <Checkbox checked={filterTypes.task} onChange={(e) => setFilterTypes((prev) => ({ ...prev, task: e.target.checked }))}>
                <Tag color="blue" style={{ borderRadius: 4, marginRight: 6 }}>
                  Tasks
                </Tag>
              </Checkbox>

              <Checkbox checked={filterTypes.meeting} onChange={(e) => setFilterTypes((prev) => ({ ...prev, meeting: e.target.checked }))}>
                <Tag color="purple" style={{ borderRadius: 4, marginRight: 6 }}>
                  Meetings
                </Tag>
              </Checkbox>

              <Checkbox checked={filterTypes.call} onChange={(e) => setFilterTypes((prev) => ({ ...prev, call: e.target.checked }))}>
                <Tag color="green" style={{ borderRadius: 4, marginRight: 6 }}>
                  Calls
                </Tag>
              </Checkbox>
            </Flex>
          </div>
        </div>
      )}

      {/* ── Sub-views: TASKS, MEETINGS, CALLS LIST ── */}
      {subTab !== "calendar" && (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", background: "#ffffff" }}>
          <Spin spinning={loading}>{activitiesForList.length === 0 ? <Empty description={`No ${subTab} scheduled yet.`} style={{ padding: "40px 0" }} /> : <Table columns={listColumns} dataSource={activitiesForList} rowKey="id" pagination={false} size="middle" />}</Spin>
        </div>
      )}

      {/* ── Schedule Activity Modal ── */}
      <Modal title="Schedule Activity" open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} destroyOnClose style={{ top: 40 }} width={480}>
        <Form form={form} layout="vertical" onFinish={handleSaveActivity} style={{ marginTop: 16 }}>
          <Form.Item name="type" label="Activity Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "call", label: "Call (Phone Interaction)" },
                { value: "meeting", label: "Meeting (Video / Demo)" },
                { value: "task", label: "Task (Follow-up)" },
              ]}
            />
          </Form.Item>

          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter title" }]}>
            <Input placeholder="e.g. Commercial Proposal Review Call" />
          </Form.Item>

          {isAdmin && employees.length > 0 && (
            <Form.Item name="employeeId" label="Assign To" initialValue={effectiveEmployeeId}>
              <Select
                showSearch
                placeholder="Select employee"
                optionFilterProp="label"
                options={employees.map((emp) => ({
                  value: emp._id || emp.id,
                  label: emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Employee",
                }))}
              />
            </Form.Item>
          )}

          {!targetCompanyName && (
            <Form.Item name="companyName" label="Company Name">
              <Input placeholder="Search or enter company name" />
            </Form.Item>
          )}

          {!targetContactName && (
            <Form.Item name="contactName" label="Contact Person">
              <Input placeholder="Enter contact person name" />
            </Form.Item>
          )}

          <Flex gap={12}>
            <Form.Item name="date" label="Date" rules={[{ required: true }]} style={{ flex: 1 }}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="time" label="Time" rules={[{ required: true }]} style={{ flex: 1 }}>
              <TimePicker format="hh:mm A" use12Hours style={{ width: "100%" }} />
            </Form.Item>
          </Flex>

          <Form.Item name="description" label="Notes / Agenda">
            <Input.TextArea rows={3} placeholder="Add discussion points, objectives, or context..." />
          </Form.Item>

          <Flex justify="flex-end" gap={10} style={{ marginTop: 24 }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving} style={{ backgroundColor: "#2459d3" }}>
              Save Activity
            </Button>
          </Flex>
        </Form>
      </Modal>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        background: "#ffffff",
      }}
      styles={{ body: { padding: 24 } }}
    >
      {content}
    </Card>
  );
}

export default ActivitiesCalendar;
